export const guidesData = [
  {
    id: 1,
    slug: "fasadmalning-trafasad-komplett-guide",
    title: "Fasadmålning av träfasad – komplett guide från förarbete till färdigt resultat",
    excerpt:
      "Att måla om en träfasad är en av de viktigaste investeringarna du kan göra för ditt hus. Här går vi igenom hela processen – från tvätt till sista strykningen.",
    readTime: "10 min",
    category: "Guide",
    image:
      "https://customer-assets.emergentagent.com/job_9628547f-dbd1-447b-abd5-f19c4c5b0b86/artifacts/i48nrj8u_Fasadmalning%209.jpg",
    content: `
Att måla om ett trähus är ett av de största underhållsprojekten du gör som villaägare. Görs det rätt håller fasaden i 12–15 år, ser snyggt ut och skyddar virket mot väder och vind. Görs det fel kan färgen börja flagna redan efter ett par år – och då är du tillbaka i samma projekt igen. I den här guiden går vi igenom **hela processen** för fasadmålning av träfasad, steg för steg, precis så som vi på [Vi Målar Sverige AB](/) arbetar med villor och bostadsrättsföreningar i Stockholm.

## Varför är fasadmålning så viktigt?

En träfasad utsätts dygnet runt för väder som sliter på färgskiktet:

- Regn och smältvatten
- UV-strålning från solen
- Temperaturväxlingar mellan varmt och kallt
- Smuts, pollen och luftföroreningar
- Alger, mögel och lavar på nord- och skuggsidor

När färgen bryts ner slutar den skydda träet. Vatten tränger in i panelen, frosten sprider sig i sprickor och till slut får du **rötskador** som kräver panelbyte. Panelbyte är flera gånger dyrare än en ommålning, och det är just därför en korrekt utförd fasadmålning ska ses som ett **skyddssystem** – inte bara kosmetik.

En välmålad fasad:

- Förlänger livslängden på panelen
- Höjer värdet på huset vid försäljning
- Minskar risken för dolda fuktskador
- Gör huset mer energieffektivt (tätt, torrt virke isolerar bättre)

## Steg 1: Besiktning och planering

Innan du sätter igång behöver fasaden besiktigas ordentligt. Det är här som kvalitetsmålare skiljer sig från snabba lösningar. Vi tittar på:

- **Färgens skick** – kritar den, flagar den, sitter den fast?
- **Panelens skick** – finns det mjuka, rötskadade eller spruckna brädor?
- **Knutar, foder och vindskivor** – ofta de första ställena där skador uppstår
- **Fönsterbleck och plåtdetaljer** – släpper vatten in där panelen möter plåten?
- **Fuktkvot i virket** – bör vara under 16 % innan målning

Om du är osäker på vad du ser rekommenderar vi en kostnadsfri besiktning. Boka via vårt [kontaktformulär](/#contact-form) så tittar vi förbi och ger dig en ärlig bedömning.

## Steg 2: Fasadtvätt – grunden till allt

En fasadmålning blir aldrig bättre än underlaget. Därför börjar vi alltid med en professionell **fasadtvätt**.

Vi använder tre olika medel beroende på situation:

- **Algtvätt** på nord- och skuggsidor där det ofta växer alger och lavar
- **Mögeltvätt** vid synliga mörka fläckar
- **Fasadtvätt** för allmän smuts, sot och luftföroreningar

Medlet appliceras, får verka in, och sköljs av med lågt tryck. **Högtryckstvätt** används försiktigt – ett för högt tryck pressar in vatten i panelen och kan till och med skada träfibrerna. Efter tvätten ska fasaden torka 2–5 dagar beroende på väder, så att fuktkvoten kommer ner under 16 %.

Tvätten gör tre saker samtidigt:

1. Tar bort smuts och alger som annars skulle "äta sig" genom den nya färgen
2. Exponerar lös färg som behöver skrapas bort
3. Ger den nya färgen **bästa möjliga fäste**

## Steg 3: Skrapning och slipning

Efter tvätten är det dags för det mest tidskrävande momentet: skrapningen. All lös, flagande eller dåligt fästande färg ska bort. Vi skrapar för hand med stålskrapor, ibland i kombination med roterande skrapor, och därefter **slipas kanterna mjuka** så att övergången mellan gammal och bar panel inte syns i slutresultatet.

Ett hus med mycket flagande färg kan kräva 40–80 timmars skrapning. Det låter mycket, men det är **exakt här** livslängden på din målning avgörs. En målare som hoppar över skrapningen målar i praktiken ovanpå ett färgskikt som redan är på väg att släppa. Resultatet ser fint ut i ett halvår – sen är du tillbaka i samma problem.

## Steg 4: Grundning och oljning

Nu kommer det som inte syns men som är avgörande:

- **Träskyddsolja / grundolja** på all bar, exponerad ved. Oljan tränger in i träet och skyddar mot fukt inifrån.
- **Grundfärg** på hela den skrapade ytan. Grundfärgen ger bra vidhäftning och jämnar ut skillnaden mellan gammalt och nytt underlag.

Ett vanligt misstag hos nybörjare är att hoppa över grundningen och gå direkt på täckfärg. Det fungerar aldrig i längden på ett trähus.

## Steg 5: Val av färg – akrylat eller täcklasyr?

Valet av färgsystem påverkar både utseende och hållbarhet. Kortversionen:

- **Akrylatfärg** – modern, slitstark, mycket bra kulörbeständighet. Passar hus som redan är målade med akrylat eller plastfärg.
- **Täcklasyr (täckande oljefärg)** – mer "levande" yta som tränger in i träet, lägre risk för flagning, passar äldre hus och traditionella miljöer.

Eftersom detta är ett ganska stort val har vi skrivit en egen artikel om det: [Akrylatfärg eller täcklasyr – vilken färg är bäst för din träfasad?](/guider/akrylatfarg-eller-tacklasyr-vilken-farg-bast). Den rekommenderar vi att du läser innan du bestämmer dig.

## Steg 6: Målning

När underarbetet är klart går själva strykningen relativt snabbt. Vi målar **minst två strykningar** täckfärg, ibland tre på söderfasader och utsatta partier.

Viktiga regler:

- Måla aldrig i direkt solljus – färgen torkar för fort och fäster sämre
- Undvik regnrisk inom 4–6 timmar efter strykning
- Lufttemperatur bör vara **+7 till +25 grader**
- Luftfuktigheten bör vara under 80 %

Vi följer väderprognosen dag för dag och planerar arbetet därefter. Vill du läsa mer om när på året det är bäst att måla, läs [När är det bäst säsong för att måla om huset?](/guider/basta-sasongen-mala-om-huset).

## Steg 7: Detaljer och slutbesiktning

När fasaden är färgsatt tar vi hand om alla detaljer:

- Fönsterfoder och knutar
- Vindskivor och takfotsbrädor
- Dörrfoder, räcken och staketanslutningar
- Plåtdetaljer och hängrännor (vid behov)

Sedan gör vi en **gemensam slutbesiktning** tillsammans med dig som kund. Vi går runt huset, tittar på varje sida, och åtgärdar direkt om något behöver justeras.

## Hur länge håller en fasadmålning?

Med ett korrekt utfört förarbete:

- **Akrylatfärg:** 12–15 år
- **Täcklasyr / oljefärg:** 8–12 år

Livslängden påverkas av väderstreck (söderfasader slits mer), närhet till hav/sjö, takutsprång och hur noggrant förarbetet utförts. Vi märker också att hus som **underhållstvättas vart 3–4 år** håller betydligt längre mellan målningar.

## Vanliga misstag vid fasadmålning

1. **För lite skrapning** – målar ovanpå lös färg
2. **Inget grundarbete** – täckfärg direkt på bar ved
3. **Fel färgsystem** – akrylat på gammal oljefärg utan förbehandling
4. **Målar på fuktig fasad** – leder till blåsor och flagning
5. **För tunt skikt** – en strykning räcker aldrig
6. **Billigaste offerten utan att läsa det finstilta** – ingår verkligen ställning, tvätt och två strykningar?

## Vad kostar det?

Priset varierar kraftigt beroende på husets storlek, skick och höjd. Vi har skrivit två detaljerade artiklar om kostnader:

- [Vad kostar fasadmålning 2026? Komplett prisguide för träfasad](/guider/vad-kostar-fasadmalning-2026-prisguide)
- [Vad kostar en fasadmålning i Stockholm?](/guider/vad-kostar-fasadmalning-stockholm)

Glöm inte **ROT-avdraget** – 30 % av arbetskostnaden dras av direkt på fakturan.

## Sammanfattning

En korrekt utförd fasadmålning på ett trähus är en investering som betalar sig själv flera gånger om:

- Skyddar virket i 12–15 år
- Höjer husets värde
- Minskar risken för dyra rötskador
- Ger ett hus du är stolt över att komma hem till

Vill du ha en **kostnadsfri besiktning** av din fasad i Stockholmsområdet? [Kontakta oss här](/#contact-form) så bokar vi in ett platsbesök. Vi är specialiserade på trähus och har målat villor och BRF-fastigheter i Stockholm i många år.
    `,
  },
  {
    id: 2,
    slug: "vad-kostar-fasadmalning-2026-prisguide",
    title: "Vad kostar fasadmålning 2026? Komplett prisguide för träfasad",
    excerpt:
      "Priset på fasadmålning varierar kraftigt beroende på husets skick, höjd och val av färg. Här går vi igenom vad som faktiskt påverkar priset – och hur du jämför offerter.",
    readTime: "9 min",
    category: "Prisguide",
    image:
      "https://customer-assets.emergentagent.com/job_9628547f-dbd1-447b-abd5-f19c4c5b0b86/artifacts/6hlhxb7j_Fasadmalning%208.jpg",
    content: `
Vad kostar det egentligen att måla om fasaden på ett trähus 2026? Det är en av de vanligaste frågorna vi får – och också den svåraste att svara på utan att ha sett huset. I den här prisguiden går vi igenom alla faktorer som påverkar priset, vilka intervall som gäller just nu, hur du jämför offerter på ett rättvist sätt och varför **det billigaste budet sällan är det bästa**.

## Genomsnittligt pris för fasadmålning 2026

För en normal villa i trä (ca 150–200 kvm fasadyta) ligger priset i Sverige 2026 på:

- **Totalpris före ROT:** 80 000 – 180 000 kr
- **Totalpris efter ROT (30 % på arbetskostnad):** 60 000 – 135 000 kr
- **Pris per kvadratmeter:** 350–750 kr/kvm
- **Timpris (inkl. ställning och material):** 650–850 kr/tim

Intervallet är brett av en anledning – två hus på pappret lika stora kan skilja 40–50 % i pris beroende på skick, höjd, färgsystem och tillgänglighet. Nedan går vi igenom varje faktor i detalj.

## 1. Fasadens skick – den största kostnadsdrivaren

Det är inte själva strykningen som kostar mest – det är **förarbetet**. Två hus med samma kvadratmeteryta kan skilja sig åt radikalt beroende på hur fasaden mår idag.

**Låg kostnad (50–100 tim förarbete):**
- Fasaden är nyligen målad (5–8 år sedan)
- Färgen sitter fast men har tappat lyster
- Inga rötskador, inga sprickor

**Hög kostnad (150–300 tim förarbete):**
- Flagande färg över stora ytor
- Knutar och foder i dåligt skick
- Panel som behöver bytas
- Alger och mögel på flera sidor

Om du är osäker på vilken kategori ditt hus tillhör, läs vår guide [När är det dags att måla om fasaden? 7 tydliga tecken](/guider/nar-dags-mala-om-fasaden-7-tecken) så får du en bra fingervisning.

## 2. Husets höjd och form

Höjd påverkar priset direkt eftersom ställning kostar tid och pengar:

- **1 plan med låga takfall:** billigast, ofta stege eller enkel ställning
- **1,5 plan:** kräver oftast rullställning eller fristående ställning
- **2 plan:** full byggställning runt hela huset
- **2,5 plan och uppåt:** betydligt dyrare – både ställning och arbetsrisk ökar

Ett hus med många vinklar, burspråk, balkonger och utbyggnader tar också längre tid än ett rektangulärt hus med samma fasadyta.

## 3. Val av färgsystem

Skillnaden i **materialkostnad** mellan akrylatfärg och täcklasyr är sällan stor – det handlar om några tusenlappar totalt på ett normalt hus. Skillnaden i **arbetstid** kan dock vara större eftersom olika system kräver olika antal strykningar och förbehandlingar.

För djupdykning: [Akrylatfärg eller täcklasyr – vilken färg är bäst?](/guider/akrylatfarg-eller-tacklasyr-vilken-farg-bast)

## 4. Förarbete – där offerter skiljer sig mest

Det här är det viktigaste att läsa i en offert. En seriös målare specificerar tydligt:

- **Tvätt** – vilka medel, hur många sidor, vilken metod
- **Skrapning** – grad av skrapning (delvis / fullständig)
- **Grundolja** på bar ved
- **Grundfärg** – minst 1 strykning
- **Täckfärg** – minst 2 strykningar
- **Detaljer** – knutar, foder, vindskivor

Om en offert är 40 % billigare än de andra så beror det nästan alltid på att något av ovanstående har **tagits bort**. Fråga specifikt: "Ingår tvätt? Ingår grundolja? Hur många strykningar täckfärg?"

## 5. Tillgänglighet

Står huset trångt mellan grannar? Finns buskage, altaner, skärmtak eller takpannor i vägen? Behöver bilar flyttas för att ställning ska kunna resas? Allt detta kostar extra tid och därmed pengar. Det är därför ett platsbesök alltid ger en mer rättvis offert än en fjärroffert via telefon.

## 6. Säsong och framförhållning

Högsäsong för fasadmålning är **april–september**. Vintermålning är i princip omöjligt på träfasad. Bokar du tidigt på året – januari till februari – är priset ofta något bättre eftersom målare gärna fyller sin kalender. Bokar du i maj när alla vill ha sitt hus målat till midsommar är det dyrare och köerna är långa.

Läs gärna [När är det bäst säsong för att måla om huset?](/guider/basta-sasongen-mala-om-huset) för en djupare genomgång av säsongsfrågan.

## ROT-avdraget – glöm inte det!

Du har rätt till **30 % ROT-avdrag på arbetskostnaden**, upp till 50 000 kr per person och år (100 000 kr för två ägare). Avdraget görs direkt på fakturan, så du ser rabatten på första räkningen.

Exempel:

| Post | Utan ROT | Med ROT |
|------|----------|---------|
| Arbetskostnad | 100 000 kr | 70 000 kr |
| Materialkostnad | 30 000 kr | 30 000 kr |
| Ställning | 15 000 kr | 15 000 kr |
| **Totalt att betala** | **145 000 kr** | **115 000 kr** |

## Vad ska en komplett offert innehålla?

När du får hem en offert, kontrollera att följande är specificerat:

1. Fasadyta i kvm
2. Antal strykningar täckfärg
3. Färgsystem (fabrikat + produkt)
4. Tvätt inkluderad
5. Skrapning inkluderad
6. Grundolja på bar ved
7. Grundfärg
8. Ställning / arbetsplattform
9. Byte av skadad panel (fast pris eller löpande)
10. Mängd arbetstimmar ungefär
11. ROT-avdraget tydligt redovisat
12. Garanti / uppföljning

Saknas flera av dessa punkter är det en röd flagga.

## Varför billigaste offerten sällan är bäst

Vi har sett det hundratals gånger: kunder som valt det lägsta budet och sedan behövt måla om hela huset 3–5 år senare. Totalkostnaden över 15 år blir då **dubbelt så hög** som om man valt en seriös målare från början.

Ett billigt pris betyder oftast en eller flera av dessa saker:

- Bara en strykning istället för två
- Ingen grundolja
- Ingen eller symbolisk skrapning
- Målning på fuktig fasad
- Obeskattat arbete (ingen ROT, ingen garanti)

Du har inga rättigheter mot en svart firma om färgen släpper. Vi på [Vi Målar Sverige AB](/) ger 5 års garanti på utfört arbete – men det förutsätter att hela systemet följs korrekt.

## Stockholm specifikt

Priserna skiljer sig åt mellan regioner. I Stockholm ligger nivåerna något högre än riksgenomsnittet. Läs vår specialguide [Vad kostar en fasadmålning i Stockholm?](/guider/vad-kostar-fasadmalning-stockholm) för lokala siffror.

## Vad kostar det att måla huset – hela huset, inte bara fasaden?

Om du räknar in tak, fönster, snickerier och altaner hamnar slutnotan högre. Läs [Vad kostar det att måla om huset?](/guider/vad-kostar-mala-om-huset) för ett helhetsperspektiv.

## Sammanfattning

- Prisspannet 2026 för en normal träfasad är **80 000 – 180 000 kr före ROT**
- Skicket på fasaden är den enskilt största prisfaktorn
- Kontrollera alltid att tvätt, skrapning, grundning och 2 strykningar ingår
- Glöm inte ROT-avdraget – 30 % direkt på fakturan
- Billig offert = kortare livslängd, högre totalkostnad över tid

Vill du ha en **kostnadsfri och detaljerad offert** på din fasad? Fyll i [kontaktformuläret](/#contact-form) så kommer vi ut och gör en ordentlig besiktning av just ditt hus. Vi är specialiserade på trähus i Stockholm och ger dig ett ärligt pris – inte ett underbud som blir dyrt i längden.
    `,
  },
  {
    id: 3,
    slug: "akrylatfarg-eller-tacklasyr-vilken-farg-bast",
    title: "Akrylatfärg eller täcklasyr – vilken färg är bäst för din träfasad?",
    excerpt:
      "Valet av färgsystem påverkar både utseende och livslängd. Här jämför vi akrylatfärg och täcklasyr i detalj – så du vet vad som passar just ditt hus.",
    readTime: "9 min",
    category: "Guide",
    image:
      "https://customer-assets.emergentagent.com/job_9628547f-dbd1-447b-abd5-f19c4c5b0b86/artifacts/hcljkqyv_Fasadmalning%207.jpg",
    content: `
När du ska måla om fasaden är valet av färgsystem en av de viktigaste besluten du tar. Det påverkar både hur huset kommer att se ut, hur lätt det är att underhålla framöver och hur länge målningen håller innan det är dags igen. De två dominerande valen för träfasader är **akrylatfärg** (modern, vattenburen plastfärg) och **täcklasyr / täckande oljefärg** (traditionellt, mer levande uttryck). I den här artikeln går vi igenom för- och nackdelar, vilken typ av hus som passar bäst för vilket system, och vad du bör undvika.

## Snabbjämförelse

| Egenskap | Akrylatfärg | Täcklasyr / oljefärg |
|----------|-------------|----------------------|
| Livslängd | 12–15 år | 8–12 år |
| Kulörhållning | Mycket god | God (kan blekna något) |
| Risk för flagning | Större om underarbetet slarvas | Mindre – flagar sällan |
| Diffusionsöppen | Mindre | Mer |
| Träkänsla | Mindre | Mer |
| Underhåll | Sällan, men större åtgärd | Oftare, men mindre åtgärd |
| Passar på gammal oljefärg | Endast efter noggrann förbehandling | Ja, direkt |

## Akrylatfärg – modern och slitstark

Akrylatfärg är en **vattenburen plastbaserad färg** som bildar ett skyddande filmskikt på panelen. Den är idag det mest sålda fasadfärgsystemet för trähus i Sverige.

### Fördelar

- **Mycket lång hållbarhet** – 12–15 år på välskött panel
- **Utmärkt kulörbeständighet** – mörka kulörer håller betydligt bättre än i oljefärg
- **Bra motstånd mot smuts och alger** – plastytan är lätt att tvätta ren
- **Snabb torktid** – arbetet går fortare och hela projektet kan färdigställas under kortare period
- **Miljövänligare att arbeta med** – vattenbaserat, mindre lösningsmedel

### Nackdelar

- **Diffusionsöppenheten är lägre** – fukt som redan är i panelen har svårare att vandra ut
- **Om den släpper flagar den ordentligt** – reparationer blir mer omfattande
- **Fäster dåligt på traditionell, tidigare oljemålad panel** om underarbetet slarvas
- **Passar sämre på gamla 1800-/tidigt 1900-talshus** som är målade med linoljefärg

### Passar bra till

- Moderna och halvmoderna villor (1960-talet och framåt)
- Fasader som redan är målade med akrylat eller plastfärg
- Hus i kustnära klimat (tål salt, vind och alger)
- Paneler i gott skick som sitter stabilt

## Täcklasyr / täckande oljefärg – traditionellt och levande

Täcklasyr är ett **mer traditionellt system** där färgen tränger in i virket snarare än att bilda en tjock film. Det ger ett mer "levande" uttryck och används ofta på äldre hus.

### Fördelar

- **Träkänsla** – man ser fortfarande strukturen i panelen
- **Mycket låg risk för flagning** – färgen släpper inte i stora flak
- **Diffusionsöppen** – panelen "andas" bättre, fukt transporteras ut
- **Enklare underhållsmålning** – en lätt tvätt + strykning räcker ofta, ingen omfattande skrapning
- **Stämmer bättre med äldre huskaraktär** – kulturhistoriskt korrekt på sekelskifteshus

### Nackdelar

- **Kortare livslängd** – 8–12 år innan nästa underhåll
- **Kulören kan blekna** – särskilt röda och mörka nyanser på söderfasader
- **Längre torktid** per strykning
- **Något mer lösningsmedel** i arbetet

### Passar bra till

- Äldre villor (före 1960)
- Hus som redan är målade med oljefärg eller linoljefärg
- Torpröda eller falurödda hus (även om vi på [Vi Målar Sverige AB](/) idag fokuserar på ljusa och gula trähus)
- Traditionella miljöer där estetiken kräver det

## Vad du absolut bör undvika

### Akrylat direkt på gammal linoljefärg utan förbehandling

Detta är en av de vanligaste anledningarna till att fasader flagnar 2–3 år efter ommålning. Akrylat fäster dåligt på matt, kritande oljefärg om du inte:

1. Tvättar ordentligt
2. Skrapar bort all lös färg
3. Grundoljar bar ved
4. Grundmålar med rätt grundfärg för övergång

### Växla fram och tillbaka mellan system

Har huset målats med täcklasyr/oljefärg de senaste gångerna är det ofta klokare att fortsätta med samma system. Byte kräver mer underarbete och ökar risken för framtida problem.

### Ljus täcklasyr med mycket vitt pigment

I ljusa kulörer får täcklasyr ibland sämre UV-skydd än akrylat. På söderfasader i sol kan det blekna tydligt.

## Kulörer och stil

Oavsett system har du i praktiken hela kulörkartan att välja mellan. Moderna akrylatsystem kan brytas i precis samma kulörer som traditionell linoljefärg. Populära val för svenska trähus 2026:

- **Varmt ljusgult / senapsgult** – klassisk svensk lantstil
- **Ljus beige / sandfärg** – tidlöst och lätt att matcha med naturen
- **Grönt i olika toner** – växer kraftigt i popularitet
- **Vitt med mörka detaljer** – modern skandinavisk look

Vi hjälper gärna till med kulörrådgivning – boka ett besök via [kontaktformuläret](/#contact-form).

## Hur länge håller färgen egentligen?

Livslängden påverkas av mycket mer än valet av system:

- **Väderstreck** – söderfasader slits 2–3 gånger snabbare än norrfasader
- **Takutsprång** – hus med långa tak skyddas mer
- **Närhet till hav/sjö** – salt och fuktbelastning kortar livslängden
- **Underarbetet** – en akrylat med dåligt underarbete håller sämre än en väl utförd täcklasyr

Vi märker också att hus som **underhållstvättas vart 3:e–4:e år** håller 20–30 % längre mellan ommålningar, oavsett vilket system som används.

## Vad kostar det?

Prisskillnaden mellan systemen är mindre än många tror. För djupdykning om kostnader, läs [Vad kostar fasadmålning 2026?](/guider/vad-kostar-fasadmalning-2026-prisguide) och den Stockholm-specifika [Vad kostar en fasadmålning i Stockholm?](/guider/vad-kostar-fasadmalning-stockholm).

## Vad rekommenderar vi?

Efter många år i branschen ser vi det så här:

- **Har du ett nyare hus (efter 1970) med befintlig plast-/akrylatfärg som sitter fast:** Välj akrylat. Bästa hållbarhet.
- **Har du ett äldre hus med oljefärg sedan generationer tillbaka:** Välj täcklasyr / oljefärg. Följ husets karaktär.
- **Är du osäker:** Ta hit en målare på besiktning. Vi tittar alltid på befintlig färg, panelens skick och väderförhållanden innan vi rekommenderar system.

## Sammanfattning

- **Akrylat** = längst livslängd, bäst kulörhållning, modernare hus
- **Täcklasyr / oljefärg** = träkänsla, mindre flagning, äldre hus
- Byt inte mellan system utan ett ordentligt underarbete
- Färgen håller längst när förarbetet är korrekt utfört – läs [komplett guide till fasadmålning av träfasad](/guider/fasadmalning-trafasad-komplett-guide) för hela processen

Vill du ha hjälp att välja rätt system för ditt hus? [Kontakta oss för en kostnadsfri besiktning](/#contact-form). Vi tittar på fasaden, befintlig färg och väderförhållanden – och rekommenderar det system som ger dig längst hållbarhet.
    `,
  },
  {
    id: 4,
    slug: "nar-dags-mala-om-fasaden-7-tecken",
    title: "När är det dags att måla om fasaden? 7 tydliga tecken",
    excerpt:
      "Många väntar för länge med att måla om fasaden. Här är de 7 signalerna du bör agera på – innan det blir dyrt.",
    readTime: "8 min",
    category: "Tips",
    image:
      "https://customer-assets.emergentagent.com/job_9628547f-dbd1-447b-abd5-f19c4c5b0b86/artifacts/r7rcizuw_Fasadmalning%206.JPG",
    content: `
En av de vanligaste frågorna vi får från villaägare är: "Hur vet jag när det är dags att måla om fasaden?" Svaret är enkelt – fasaden talar om det, om du vet vad du ska titta efter. Den som väntar för länge får ofta betala **dubbelt så mycket** när det väl är dags, eftersom skadorna hunnit sprida sig ner i virket. I den här artikeln går vi igenom de sju tydliga signalerna på att din träfasad behöver ommålas, varför de uppstår och hur mycket varje signal brukar kosta om du agerar i tid jämfört med om du väntar.

## 1. Färgen kritar

Prova det här: dra handen över fasaden på söderfasaden en torr dag. Får du ett vitt/färgat lager på handen? Då **kritar** färgen. Det är det första tydliga tecknet på att färgskiktet börjar brytas ner av UV-ljus.

Vad som händer rent tekniskt: solen bryter ner bindemedlet i färgen, och pigmentet lossnar som ett mycket tunt pulver. Så länge det bara kritar lätt är färgen fortfarande delvis skyddande – men tiden att agera är **nu**. Om du målar om när fasaden bara kritar räcker det oftast med en tvätt och två strykningar. Väntar du ytterligare 2–3 år tillkommer ofta omfattande skrapning, vilket ökar kostnaden med 30–50 %.

## 2. Sprickor i färgskiktet

Tittar du nära på panelen och ser **hårfina sprickor i färgskiktet** – ofta vinkelrätt mot fibrerna – är det ett tydligt alarm. Sprickor släpper in vatten. Vatten som inte kommer ut orsakar rötskador.

Sprickor uppstår oftast av tre anledningar:
- Färgen har åldrats och blivit spröd
- Färgen har lagts i för tjockt skikt en gång i tiden
- Felaktigt färgsystem har kombinerats ovanpå varandra

När du ser sprickor är det **sista chansen** att komma undan billigt. Vänta du ett par vintrar till har fukten hunnit djupt ner i panelen och panelbyten kan bli nödvändiga.

## 3. Flagande färg

När färgen inte bara spricker utan faktiskt **lossnar i flak** är skyddsskiktet brutet. Nu regnar vatten rakt in i virket. Varje regn och varje natt under fryspunkten bidrar till att skadan sprider sig.

Flagande färg hittar du oftast:

- På söderfasader (mest UV-exponering)
- Runt fönsterfoder och knutar
- Nedanför fönsterbleck där vatten droppar
- Vid takfoten där det blåser in regn

Ju tidigare du agerar, desto mindre behöver skrapas bort. Läs mer om hur vi hanterar detta i [fasadmålning av träfasad – komplett guide](/guider/fasadmalning-trafasad-komplett-guide).

## 4. Mörka fläckar – alger, mögel eller lav

**Gröna, svarta eller gråa fläckar** på norr- och skuggsidor betyder att fasaden är biologiskt angripen. Det är inte livshotande för huset, men det är en signal om att färgskiktet har börjat bli poröst och tar upp fukt.

Två viktiga saker att veta:

1. Alger och mögel som lämnas kvar **äter sig genom nytt färgskikt** om du bara målar över. Därför måste en professionell algtvätt alltid göras före ommålning.
2. Om problemet sprider sig snabbt kan det vara ett tecken på att takutsprånget är för kort, eller att hängrännor behöver rensas.

Om du har fått mörka fläckar på mindre än 3 år sedan senaste ommålningen – ring oss. Något är fel i systemet.

## 5. Torrt, grått och sprucket trä

Det värsta scenariot: du ser **bar ved**, ofta grå eller silverfärgad, och virket känns torrt och sprucket. Färgen har släppt helt. Vattnet går nu direkt in i panelen och UV-ljuset bryter ner själva träfibrerna.

Om du fortfarande kan se solida, friska träfibrer är det fortfarande räddningsbart med grundolja + grundfärg + 2 strykningar. Om du kan sticka in en skruvmejsel utan motstånd är virket däremot angripet av röta – då behöver panelen bytas ut innan målning.

Det här är läget då **vänta** blir mycket dyrt. En ommålning i det här skicket kan kosta 50–100 % mer än om du målat för 2–3 år sedan.

## 6. Det var mer än 10–15 år sedan huset målades sist

Även om fasaden ser helt okej ut bör du göra en **noggrann besiktning** om det gått 10–15 år sedan senaste ommålningen. På söderfasader kan bindemedlet vara mer nedbrutet än det syns på ytan. Bättre att upptäcka det nu, planera och boka in målning till nästa säsong, än att stå där med flagande fasad om ett år.

Att **planera fasadmålning i förväg** ger dig också:

- Möjlighet att välja säsong själv – läs [När är det bäst säsong för att måla om huset?](/guider/basta-sasongen-mala-om-huset)
- Flera offerter att jämföra – läs [Vad kostar fasadmålning 2026?](/guider/vad-kostar-fasadmalning-2026-prisguide)
- Tid att budgetera (och utnyttja ROT-avdraget)

## 7. Färgen har tappat lyster och glans

Sista signalen är mer estetisk, men betydelsefull: färgen har bleknat, blivit matt och "tråkig". Söderfasaden är tydligt blekare än norrfasaden. Kulören är inte den du valde för 12 år sedan.

Detta är inte akut ur skyddsperspektiv, men det är ett tecken på att **pigmenten bryts ner**, och nedbrytningen av bindemedel (steg 1 – kritning) är oftast nära. Detta är ofta den bästa tidpunkten att boka om, eftersom skicket fortfarande tillåter minimal skrapning och snabbt arbete.

## Bonus: Knutar, foder och vindskivor

Innan du bedömer huset i stort – titta på **detaljerna**:

- Knutlådor och hörn
- Fönsterfoder
- Dörrfoder
- Vindskivor och takfotsbrädor

Dessa punkter slits **2–3 gånger snabbare** än själva fasadpanelen eftersom de är mer exponerade. Om knutarna är i dåligt skick men panelen ser bra ut, kan det räcka med en partiell underhållsmålning som en mellanåtgärd.

## Så här agerar du

1. Gå runt huset en solig eftermiddag
2. Dra handen över söderfasaden – kritar den?
3. Titta på panelens kanter – spricker det, lossnar det?
4. Gå till norrsidan – ser du gröna eller mörka fläckar?
5. Inspektera knutar och fönsterfoder
6. Fråga dig själv: "När målade vi sist?"

Ser du två eller fler signaler är det dags att börja planera. Ser du fyra eller fler **bör du agera inom 6–12 månader**.

## Vad händer om du väntar?

- **1–2 år extra:** ofta 20–30 % högre kostnad
- **3–5 år extra:** panelbyten kan bli aktuella; totalkostnaden kan dubbleras
- **5+ år extra:** risk för dolda rötskador, mögel inne i väggkonstruktionen, fuktskador i isolering

Den som tror att man "sparar pengar" genom att vänta glömmer ofta bort att en fasadmålning går från att vara **underhåll** till att bli **renovering** om man väntar för länge.

## Sammanfattning

De 7 tecknen du ska hålla utkik efter:

1. Färgen kritar
2. Sprickor i färgskiktet
3. Flagande färg
4. Mörka fläckar (alger/mögel)
5. Torrt, grått och sprucket trä
6. Mer än 10–15 år sedan senaste målningen
7. Färgen har tappat lyster

Vill du ha en **ärlig, kostnadsfri besiktning** där vi tittar på din fasad och säger om det är dags? Vi gör sånt varje vecka i Stockholm. [Boka via kontaktformuläret här](/#contact-form) så bokar vi ett besök som passar dig. Vi är specialiserade på trähus och berättar ärligt om det är läge att måla nu – eller om du kan vänta ett år till.
    `,
  },
  {
    id: 5,
    slug: "vad-kostar-fasadmalning-stockholm",
    title: "Vad kostar en fasadmålning i Stockholm? Lokal prisguide 2026",
    excerpt:
      "Priserna för fasadmålning i Stockholm skiljer sig från riksgenomsnittet. Här går vi igenom lokala nivåer, exempel från villor i olika områden och varför läget påverkar priset.",
    readTime: "9 min",
    category: "Prisguide",
    image:
      "https://customer-assets.emergentagent.com/job_9628547f-dbd1-447b-abd5-f19c4c5b0b86/artifacts/8ewxw6kd_Fasadmalning%201.jpg",
    content: `
Bor du i Stockholm och funderar på att måla om ditt trähus? Då är det viktigt att veta att prisnivåerna i huvudstadsregionen skiljer sig märkbart från resten av Sverige. Det beror dels på högre löne- och driftkostnader, dels på logistiska utmaningar i tätbebyggda områden och dels på att många hus här står i utsatta vind- och väderlägen. I den här artikeln går vi igenom vad en fasadmålning faktiskt kostar i Stockholm 2026 – med lokala exempel från villor i olika områden och konkreta råd för dig som vill ha en rättvis offert.

## Lokala prisnivåer 2026

För ett normalt trähus (ca 150–200 kvm fasadyta) i Stockholmsregionen ligger priset 2026 i intervallet:

- **Totalpris före ROT:** 95 000 – 210 000 kr
- **Totalpris efter ROT (30 % på arbetskostnad):** 72 000 – 155 000 kr
- **Pris per kvadratmeter:** 420–850 kr/kvm
- **Timpris (inkl. ställning och material):** 700–900 kr/tim

Jämfört med riksgenomsnittet (som vi gick igenom i [Vad kostar fasadmålning 2026?](/guider/vad-kostar-fasadmalning-2026-prisguide)) ligger Stockholm ungefär **10–15 % högre**. Det är normalt för hantverkstjänster i regionen och beror främst på tre faktorer som vi går igenom nedan.

## Varför är det dyrare i Stockholm?

### 1. Högre lönekostnader

En erfaren målare i Stockholm har en helt annan timkostnad än motsvarande i mindre orter. Kollektivavtal, sociala avgifter, försäkringar och yrkesutbildningar landar på en nivå som gör att seriösa målare inte kan gå under cirka 650–700 kr/tim i kundpris ens med ROT. Ett pris som ligger väsentligt under det innebär ofta obeskattat arbete – och då har du ingen garanti om något går fel.

### 2. Logistik och tillgänglighet

Stockholm är tätbyggt. Det påverkar arbetet på flera sätt:

- **Parkering** – ofta krävs parkeringstillstånd eller avgift
- **Ställning** – smala gator och grannhus gör ställningsmontage mer komplicerat
- **Leveranser** – färg, virke och material tar längre tid att hämta och ställa upp
- **Trafikmiljö** – mycket arbete sker vid trafikerade vägar, vilket kräver extra skyltning och säkerhet

Ett villaområde i Enskede eller Älvsjö har helt andra förutsättningar än ett fristående hus på landet. Därför tar ett "standardjobb" i Stockholm ofta 10–20 % mer tid än samma jobb utanför storstaden.

### 3. Väderbelastning

Hus nära vatten (Saltsjöbaden, Lidingö, Värmdö) och på Stockholms utsatta berg och höjder får ofta mer slitage från vind, salt och fukt. Det innebär mer förarbete och oftare tätare underhållsintervall.

## Exempel på riktiga jobb

Nedan ger vi ungefärliga prisintervall baserat på verkliga projekt vi utfört i olika delar av regionen. Exakta siffror beror alltid på skicket, men detta ger dig en känsla.

### Villa i Enskede – 1,5 plan, ca 160 kvm fasadyta, normal skick

- Tvätt, skrapning, grundolja + grundfärg, 2 strykningar akrylat
- Inkl. ställning på långsidorna, stege på kortsidorna
- **Prisspann:** 110 000 – 140 000 kr före ROT

### Villa i Bromma – 2 plan, ca 220 kvm fasadyta, måttligt förarbete

- Full byggställning runt hela huset
- Tvätt, omfattande skrapning, grundolja + grundfärg, 2 strykningar
- Byte av 4 st panelbräder inkluderat
- **Prisspann:** 170 000 – 210 000 kr före ROT

### Villa i Lidingö – 1 plan, ca 140 kvm fasadyta, nära vatten

- Kräver saltbeständig fasadtvätt (algnivå ofta hög)
- Omfattande tvätt, normal skrapning, grundolja, 2 strykningar täcklasyr
- **Prisspann:** 95 000 – 125 000 kr före ROT

### BRF-hus i Södermalm – 3 plan, ca 450 kvm träfasad

- Full byggställning, arbete enligt bygglov
- Tvätt, skrapning, grundning, 2 strykningar akrylat
- **Prisspann:** 450 000 – 600 000 kr före ROT

Dessa siffror varierar alltid i det enskilda fallet, men de ger en bra fingervisning om var nivåerna ligger.

## ROT-avdraget i Stockholm

ROT-avdraget fungerar exakt likadant i hela Sverige: **30 % av arbetskostnaden**, upp till 50 000 kr per person och år. Eftersom arbetskostnaden i Stockholm är något högre än riksgenomsnittet blir också **ROT-avdraget i absoluta kronor ofta större** här. På ett normalt villajobb sparar du i regel 25 000–45 000 kr via ROT.

Exempel på fakturarad:

| Post | Utan ROT | Med ROT |
|------|----------|---------|
| Arbetskostnad | 120 000 kr | 84 000 kr |
| Material | 30 000 kr | 30 000 kr |
| Ställning | 20 000 kr | 20 000 kr |
| **Totalt** | **170 000 kr** | **134 000 kr** |

## Därför varierar offerterna så mycket

Vi hör ofta från kunder som fått tre offerter i Stockholm där den högsta är **dubbelt så dyr** som den lägsta. Det kan verka orimligt, men det är nästan alltid förklarligt:

- **Lägsta offerten** har ofta inte räknat med full ställning, bara partiell skrapning och en strykning
- **Mellannivån** är en rimlig offert med komplett underarbete och 2 strykningar
- **Högsta offerten** innehåller ofta extra: panelbyten, 3 strykningar, premiumfärg, längre garanti

När du jämför offerter, gå igenom vår checklista från [prisguiden](/guider/vad-kostar-fasadmalning-2026-prisguide):

1. Fasadyta i kvm
2. Antal strykningar
3. Ställning inkluderad
4. Tvätt och skrapning
5. Grundolja + grundfärg
6. Skadebyten (fast pris eller löpande?)
7. ROT tydligt redovisat

## Lokala faktorer som ökar priset

### Kulturskyddade fastigheter och k-märkning

Bor du i ett område med kulturskyddsbestämmelser (vissa delar av Djursholm, Saltsjöbaden, Bromma) kan du behöva specifik färgtyp, kulörmatchning och ibland bygglov för ommålning. Det kan kräva 10–20 % extra i tid.

### Höjd och våningsantal

I Stockholm är många villor 1,5–2 plan. Varje extra våning innebär:
- Större ställning
- Fler arbetsdagar
- Högre arbetsrisk – och därmed högre försäkringskostnad

Ett typiskt 1,5-planshus i Enskede kostar ca 30 % mer än ett likvärdigt 1-planshus.

### Närhet till trafik

Arbetar vi vid en gata med tung trafik krävs ofta trafikskyltning, ibland TA-plan (trafikanordningsplan). Detta tillkommer som en extra kostnad, oftast 5 000–15 000 kr.

## Hur får du ett rättvist pris i Stockholm?

Vårt råd efter många år i branschen:

1. **Ta in minst 3 offerter** från etablerade företag med F-skatt och försäkring
2. **Kräv platsbesök** – offerter via telefon är nästan alltid för grova för Stockholms förhållanden
3. **Jämför äpplen med äpplen** – samma yta, samma antal strykningar, samma färgsystem
4. **Lita inte blint på lägsta priset** – läs [När är det dags att måla om fasaden?](/guider/nar-dags-mala-om-fasaden-7-tecken) så förstår du varför dåligt underarbete kostar dubbelt i längden
5. **Boka tidigt på året** – januari–mars är bästa tid att säkra en bra målare för sommarhögsäsongen

## När under året ska man måla i Stockholm?

Stockholms målningssäsong är kort – ungefär **slutet av april till början av oktober**. Det ger ca 5 månader effektiv måltid, och konkurrensen om bra målare är hård. Läs [När är det bäst säsong för att måla om huset?](/guider/basta-sasongen-mala-om-huset) för konkreta råd om timing.

## Vi Målar Sverige AB i Stockholm

Vi på [Vi Målar Sverige AB](/) är specialiserade på **fasadmålning av trähus i Stockholmsområdet**. Vi arbetar både med villor och BRF-fastigheter, och har under åren färdigställt hundratals projekt i allt från Enskede och Älvsjö till Lidingö, Nacka, Värmdö, Bromma och Sollentuna.

Vi lämnar alltid:

- Kostnadsfri besiktning och offert på plats
- Fast pris – inga överraskningar på fakturan
- F-skatt, försäkring och kollektivavtal
- 5 års garanti på utfört arbete
- ROT-avdrag dras av direkt på fakturan

## Sammanfattning

- Fasadmålning i Stockholm kostar **95 000 – 210 000 kr** för en normal villa, 10–15 % över riksgenomsnittet
- Priset drivs upp av löner, logistik och väderbelastning lokalt
- ROT-avdraget sparar dig typiskt 25 000–45 000 kr
- Ta alltid in minst 3 offerter baserade på platsbesök, inte telefonsamtal
- Välj en etablerad målare med F-skatt, försäkring och garanti – billigt blir ofta dyrast i längden

Vill du ha en **kostnadsfri offert** från oss? Fyll i vårt [kontaktformulär](/#contact-form) så kommer vi ut på besiktning inom några dagar. Vi ger dig ett tydligt, skriftligt pris och ett underlag du faktiskt kan jämföra med andra offerter.
    `,
  },
  {
    id: 6,
    slug: "vad-kostar-mala-om-huset",
    title: "Vad kostar det att måla om huset? Helhetsguide för trähus 2026",
    excerpt:
      "Att måla om huset är mer än bara fasaden. Här går vi igenom totalkostnaden – fasad, fönster, snickerier, tak och altan – och hur du planerar budget smart.",
    readTime: "10 min",
    category: "Prisguide",
    image:
      "https://customer-assets.emergentagent.com/job_9628547f-dbd1-447b-abd5-f19c4c5b0b86/artifacts/je1anxka_Fasadmalning%203.jpg",
    content: `
När folk frågar "vad kostar det att måla om huset?" menar de ofta något mer än bara fasaden. Ett trähus är en helhet – fasad, fönster, knutar, foder, vindskivor, altanräcken, staket och ibland taket. Ska allt se snyggt ut och hålla i 10–15 år måste man planera för helheten. I den här artikeln går vi igenom **vad det kostar att måla om hela huset 2026**, hur du kan dela upp projektet i faser och var du kan spara pengar utan att det drabbar kvaliteten.

## Vad ingår i "hela huset"?

När vi på [Vi Målar Sverige AB](/) pratar om att måla om hela huset menar vi normalt följande delmoment:

1. **Fasad** – själva panelen, den största ytan
2. **Knutar och fönsterfoder** – utsatta detaljer som slits snabbare än panelen
3. **Vindskivor och takfotsbrädor** – ofta bortglömda, men viktiga
4. **Fönster** – karmar, bågar, spröjs (oftast linoljefärg)
5. **Dörrar** – ytter- och förrådsdörrar
6. **Altanräcken, altan- och staketdelar** – utsätts hårt för sol och nederbörd
7. **Taktäckning** – plåt eller eternitliknande material kan målas, betongpannor oftast inte

Alla dessa har olika färgsystem, olika livslängd och olika kostnadsnivåer per kvadratmeter.

## Totalkostnad för "hela huset" 2026

För en normalstor villa (1,5 plan, ca 160 kvm fasadyta, 15–20 fönster, altan) landar totalkostnaden för ett fullständigt målningsprojekt 2026 normalt på:

- **Grundnivå (fasad + knutar/foder):** 95 000 – 140 000 kr före ROT
- **Mellannivå (+ fönster + dörrar):** 140 000 – 210 000 kr före ROT
- **Fullservice (+ altan + räcken + tak):** 200 000 – 310 000 kr före ROT
- **Stort, utsatt 2-plans hus:** 300 000 – 450 000 kr före ROT

Efter ROT-avdrag (30 % på arbetskostnad, max 50 000 kr/person/år) landar slutnotan oftast 25–45 000 kr lägre. Läs mer om [prisnivåer 2026](/guider/vad-kostar-fasadmalning-2026-prisguide) och [lokala priser i Stockholm](/guider/vad-kostar-fasadmalning-stockholm).

## Kostnad per delmoment

### Fasad (största posten)

För en typvilla ligger enbart fasadmålningen på **80 000 – 180 000 kr före ROT**, beroende på höjd, skick och färgsystem. Detta är hela kedjan: tvätt, skrapning, grundolja, grundfärg och två strykningar täckfärg.

### Knutar och fönsterfoder

Dessa är ofta i sämre skick än panelen, eftersom de utsätts mer för väder. Normalt ingår de i fasadofferten, men om separat:

- **Endast knutar + foder på normal villa:** 15 000 – 35 000 kr

### Vindskivor och takfotsbrädor

- **Målning av vindskivor/takfot:** 8 000 – 20 000 kr

### Fönstermålning

Detta är en kostnadsdrivare som många underskattar. Fönster kräver:

- Nedmontering och demontering av kittfals
- Eventuellt byte av kitt
- Slipning och grundning
- 2 strykningar linoljefärg (helst)

Pris per fönsterluft (ruta) ligger 2026 på:

- **Enkel fönsterluft (fristående bågar):** 1 500 – 3 000 kr
- **Kopplat 2-glasfönster med spröjs:** 2 500 – 5 000 kr
- **Renovering av fönster med kittbyte:** 5 000 – 10 000 kr

För ett hus med 15 fönster kan fönsterarbetet alltså kosta 40 000 – 120 000 kr isolerat.

### Dörrar

- **Målning av ytterdörr (2 strykningar):** 3 500 – 7 000 kr
- **Komplett renovering inkl. slipning:** 6 000 – 12 000 kr

### Altan och räcken

- **Altanräcke normalstorlek:** 12 000 – 30 000 kr
- **Altangolv (oljning / betsning):** 8 000 – 25 000 kr

### Staket

- **Enkelt trästaket, 30 löpmeter:** 15 000 – 35 000 kr

### Tak

- **Målning av plåttak, villa:** 35 000 – 80 000 kr
- Betongpannor: målas sällan, kan tvättas istället

## Kan man dela upp projektet?

Ja – och det är ofta klokt. I praktiken går många kunder i **tre faser**:

**Fas 1 (år 1):** Fasad + knutar + foder + vindskivor
**Fas 2 (år 2–3):** Fönster + dörrar
**Fas 3 (år 3–5):** Altan + staket + eventuellt tak

Fördelar med uppdelning:

- Bättre för budgeten
- Utnyttjar ROT-avdraget över flera år
- Kan göras när varje del verkligen behöver det

Nackdelar:

- Något högre totalkostnad (etableringskostnader återkommer)
- Mer planering krävs

Vi hjälper gärna till med en underhållsplan där vi tittar på vilka delar som är mest akuta. [Boka kostnadsfri besiktning här](/#contact-form).

## Exempel: "medelvillan"

Låt oss räkna på ett konkret, typiskt exempel. En 1,5-plansvilla från 1950-talet i Stockholm med:

- 160 kvm fasadyta (akrylat)
- Knutar, foder, vindskivor
- 14 fönster (kopplade 2-glas med spröjs)
- 1 ytterdörr + 1 förrådsdörr
- Altan med räcke (20 löpmeter)
- 30 meter trästaket

**Fullständigt projekt i ett svep:**

| Del | Pris före ROT |
|-----|---------------|
| Fasad inkl. knutar/foder/vindskivor | 130 000 kr |
| 14 fönster (renovering + 2 strykningar) | 70 000 kr |
| 2 dörrar | 10 000 kr |
| Altan inkl. räcke | 25 000 kr |
| Staket | 20 000 kr |
| **Total före ROT** | **255 000 kr** |
| ROT-avdrag (30 % av ~180 000 kr arbete) | ca −54 000 kr (max 50 000 kr/person/år) |
| **Slutpris (1 ägare):** | ca 205 000 kr |
| **Slutpris (2 ägare):** | ca 155 000 kr |

Om samma projekt delas över 2 år får du utnyttja ROT-avdraget fullt ut båda åren och landar alltså totalt sett något lägre – om det är två ägare på fastigheten.

## Var kan du spara pengar?

### Gör själv det som är säkert att göra själv

- **Måla altan, staket, förrådsdörrar** – om du har tid och tålamod är detta jobb som inte kräver stege eller ställning
- **Underhållstvätta** vart 3:e år mellan ommålningar – förlänger livslängd markant

### Gör INTE själv:

- Fasadmålning på höga hus (fallrisk, försäkringsproblem)
- Fönsterrenovering (specialkunskap om kitt, linolja)
- Grundarbete på omfattande fasadskador

### Välj rätt system från början

Ett korrekt utfört arbete håller 12–15 år. Ett slarvigt utfört arbete håller 3–5 år. Totalkostnaden över 30 år kan skilja **300–400 000 kr** för ett normalt hus. Läs [komplett guide till fasadmålning av träfasad](/guider/fasadmalning-trafasad-komplett-guide) för hur det ska göras rätt.

### Planera säsong rätt

Jobbar du med hela projektet under en säsong kommer du få offerter kring maj–juni. Bokar du i januari–februari ligger priserna ofta 5–10 % lägre och tillgången är bättre. Läs [När är det bäst säsong för att måla om huset?](/guider/basta-sasongen-mala-om-huset).

### Välj rätt färgsystem

Billig färg från byggvaruhus är sällan en besparing – arbetskostnaden är densamma, men färgen håller hälften så länge. Läs [Akrylatfärg eller täcklasyr – vilken färg är bäst?](/guider/akrylatfarg-eller-tacklasyr-vilken-farg-bast).

## ROT-avdraget över flera år

En viktig tanke: **ROT-avdraget är 50 000 kr per person per år**. Ett par som äger huset tillsammans har alltså 100 000 kr/år att utnyttja.

På stora projekt över 300 000 kr kan det vara smart att dela upp i två år för att maximera avdraget.

## Hur lång tid tar ett helhetsprojekt?

För ett typiskt fullt projekt med en målarfirma på 2–3 man:

- **Fasad:** 3–5 veckor
- **Fönster (14 st):** 2–4 veckor
- **Altan + räcken + staket:** 1–2 veckor
- **Totalt:** 6–11 veckor effektiv arbetstid

Med vädret i Sverige innebär det i praktiken **en hel säsong** (maj–september) om allt ska klaras på en säsong. Därför är uppdelning över 2 år ofta realistiskt.

## Sammanfattning

- "Hela huset" på normal villa kostar **95 000 – 310 000 kr** före ROT 2026, beroende på omfattning
- Fasaden är den största posten; fönster är den mest underskattade
- Planera i faser över 2–3 år för att maximera ROT och hantera budgeten
- Välj rätt färgsystem från början – totalkostnad över 30 år styrs mer av hållbarhet än av dagspriset
- Gör själv där du kan (altan, staket, tvätt), men överlåt fasad, fönster och höga partier till proffs

Vi på [Vi Målar Sverige AB](/) hjälper dig gärna med en **helhetsplan för ditt hus**. Vi besiktigar gratis, sätter samman en underhållsplan och ger dig transparenta priser för varje del. [Boka besiktning här](/#contact-form) så tittar vi på ditt hus.
    `,
  },
  {
    id: 7,
    slug: "basta-sasongen-mala-om-huset",
    title: "När är det bäst säsong för att måla om huset? Så väljer du rätt tid",
    excerpt:
      "Säsongen spelar stor roll för både kvalitet och pris. Här går vi igenom när det är bäst att måla om trähus i Sverige – och varför det är smart att boka tidigt.",
    readTime: "8 min",
    category: "Guide",
    image:
      "https://customer-assets.emergentagent.com/job_9628547f-dbd1-447b-abd5-f19c4c5b0b86/artifacts/kuwlvd5b_Fasadmalning%202.jpg",
    content: `
Få saker påverkar kvaliteten på en fasadmålning så mycket som vädret och tidpunkten. Målar du vid rätt temperatur och luftfuktighet får du ett resultat som håller i 12–15 år. Målar du vid fel tidpunkt – för kallt, för varmt, för fuktigt, för soligt – kan färgen börja flagna redan efter ett par år. I den här artikeln går vi igenom vad som faktiskt är den bästa säsongen för att måla om huset i Sverige, hur olika delar av säsongen passar olika moment, och varför det lönar sig att boka i god tid.

## Snabbsvaret: maj till september

Den praktiska säsongen för fasadmålning i Sverige sträcker sig från **slutet av april till början av oktober**. Optimal period är **maj till mitten av september**, med bästa förhållanden ofta i **juni, juli och augusti**.

Men det är långt ifrån hela sanningen. Inom den perioden finns både bra och dåliga veckor – och vissa moment passar faktiskt bättre tidigt eller sent på säsongen. Låt oss gå igenom det i detalj.

## Vilka förhållanden kräver fasadfärg?

De flesta fasadfärger för trähus – både akrylat och täcklasyr – kräver följande för att fästa korrekt:

- **Temperatur:** +7 °C till +25 °C, både i luft och på underlaget
- **Luftfuktighet:** under 80 % relativ fuktighet
- **Nederbörd:** inget regn inom 4–6 timmar efter strykning (helst inget samma dygn)
- **Fuktkvot i virket:** under 16 %
- **Direktsol:** undvik att måla i direkt solljus på söderfasad (färgen torkar för snabbt och fäster sämre)

Alla dessa punkter måste vara uppfyllda **samtidigt** för att målningen ska bli bra. I Sverige är det här som begränsar säsongen.

## Säsongen månad för månad

### April – tidig men riskabel

I april kan enstaka dagar vara milda och soliga, men:

- Nätterna är ofta under +5 °C
- Virket är sällan torrt nog (fuktkvot ofta 18–22 %)
- Morgondagg ligger länge

**Bäst för:** förberedelser, bygga ställning, tvätt (om milda dygn), planering.

### Maj – säsongen öppnar på riktigt

Mitten av maj brukar vara startpunkt för effektiv fasadmålning i större delen av Sverige.

- Dagstemperaturer 12–20 °C
- Nattemperaturer stadigt över +5 °C
- Virket hinner torka under dagen

**Bäst för:** grundarbete, skrapning, tvätt, grundfärg.

### Juni – förmodligen bästa månaden

- Långa dagar ger mycket arbetstid
- Stabilt varmt och torrt
- Kvällar och nätter fortfarande milda

**Bäst för:** hela processen – tvätt, grundfärg, 2 strykningar.

### Juli – varm men krävande

- Mycket varmt, ibland över +25 °C på söderfasaden
- Direktsol försvårar arbete på solsidor
- Hårdare krav på att arbetslaget jobbar i rätt ordning runt huset

**Bäst för:** norr- och östsidor på dagen, söderfasader tidig morgon och sen eftermiddag.

### Augusti – en av de absolut bästa månaderna

- Fortfarande stabilt varmt, men mindre extrem hetta
- Vegetationen är torr, mindre pollen och blomdamm
- Ofta torrt väder under långa perioder

**Bäst för:** hela processen, särskilt färdigstrykning.

### September – sista fullvärdiga månaden

- Behaglig temperatur, fortfarande över +10 °C på dagen
- Kortare dagar
- Morgondagg kan försena starttiden

**Bäst för:** andra strykningen och slutarbeten, men undvik att starta nya stora projekt i mitten av månaden.

### Oktober – gränsfallet

- Dagar kan vara milda, men nätter närmar sig fryspunkten
- Hög risk för regn
- Fuktkvoten i virket stiger

**Bäst för:** endast enstaka kompletteringsarbeten, inomhusmåleri, slutbesiktning.

### November – mars

Utomhusmåleri på träfasad är inte möjligt av kvalitetsskäl. Vi rekommenderar aldrig detta, oavsett vad andra firmor lovar.

## Regionala skillnader i Sverige

- **Stockholm och Mälardalen:** praktisk säsong slutet april – början oktober
- **Skåne och Västra Götaland:** något längre säsong, fram till mitten av oktober
- **Norrland:** kortare, oftast slutet maj – mitten september

Bor du i Stockholm, läs gärna [Vad kostar en fasadmålning i Stockholm?](/guider/vad-kostar-fasadmalning-stockholm) för lokala priser och hur säsongen där fungerar.

## När ska du boka målaren?

Det här är det viktigaste i hela artikeln. **Högsäsong är kraftigt överbokad**, och seriösa målare är fullbokade flera månader i förväg.

Idealt tidsschema:

- **Januari – mars:** boka ditt projekt för kommande säsong
- **April:** besiktning, offert, kontrakt, beställ färg
- **Maj – september:** utförande
- **Oktober:** slutbesiktning och eftermarknad

Bokar du i februari får du:

- Bästa urvalet av datum
- Möjlighet att förhandla pris
- Tid att tänka igenom färgval

Bokar du i juni för samma säsong:

- Troligen fullbokat hos de bästa firmorna
- Begränsat val av datum
- Ofta högre pris på grund av efterfrågan

## Vad händer om det börjar regna mitt i ett projekt?

Regn är normalt under en svensk sommar. En seriös målare:

- Läser väderprognos 5–7 dagar framåt
- Planerar strykningar efter torra fönster
- Pausar och återkommer vid behov
- Täcker över nymålade ytor om kraftigt regn hotar inom 2 timmar

Ett avbrott på 1–2 dagar kostar inte kunden något hos oss – det ingår i seriös planering. Läs mer om vår process i [fasadmålning av träfasad – komplett guide](/guider/fasadmalning-trafasad-komplett-guide).

## Kan man måla vid hög luftfuktighet?

Nej. Luftfuktighet över 80 % betyder att färgen torkar sakta, fibrerna reser sig, och det finns hög risk för att färgen aldrig fäster ordentligt. Typiska situationer att undvika:

- Tidig morgon (innan dagg brunnit av)
- Kvällar när daggpunkten närmar sig
- Dagen efter regn (virket är ofta fortfarande fuktigt)
- Augustikvällar vid sjö

## Signaler på att det är rätt tid för dig att boka

Oavsett säsong är det dags att kontakta målaren när:

- Fasaden visar tecken på nedbrytning – läs [När är det dags att måla om fasaden? 7 tecken](/guider/nar-dags-mala-om-fasaden-7-tecken)
- Det gått 10–15 år sedan senaste ommålningen
- Du planerar att sälja huset inom 2 år
- Du vill utnyttja ROT-avdraget detta år

## Vintermålning – vad sägs om det?

Vissa målare erbjuder "vintermålning" med snabbtorkande system. Vårt ärliga svar: **avstå om du äger ett trähus**. Resultatet håller sällan mer än 3–5 år, och du får sällan garanti. Det är bättre att vänta till nästa säsong.

## Hur ska du planera din budget?

Säsongen påverkar också plånboken. I riket som helhet ser vi:

- **Lågsäsong för bokning (januari–mars):** priser ofta 5–10 % lägre
- **Mellansäsong (april):** normala priser
- **Högsäsong (maj–juli):** priser i toppnivån
- **Slut av säsong (augusti–september):** priser kan förhandlas ner om det finns luckor

Läs vår fullständiga [prisguide för 2026](/guider/vad-kostar-fasadmalning-2026-prisguide) och vår [helhetsguide för att måla om huset](/guider/vad-kostar-mala-om-huset).

## Sammanfattning

- **Bästa säsong:** maj till september, med juni och augusti som toppmånader
- **Boka i god tid** – januari–mars är optimalt för kommande säsong
- **Undvik:** temperaturer under +7 °C, luftfuktighet över 80 %, direkt regn inom 6 timmar
- **Vintermåla inte** utomhus på träfasad
- **Högsäsong är fullbokad** – planera flera månader i förväg

Vill du säkra en plats i säsongen 2026? [Kontakta oss nu](/#contact-form) så bokar vi en kostnadsfri besiktning och lägger upp ett förslag på tidsschema. Vi på [Vi Målar Sverige AB](/) är specialiserade på trähus i Stockholmsområdet och planerar alltid våra projekt efter väderfönstret – inte efter kalendern.
    `,
  },
];
